/**
 * Selector de vehículos y pasajeros del formulario de reserva.
 * Botones para incrementar/decrementar contadores de cada tipo.
 */
import { Minus, Car, Bike as Motorcycle, Truck, Bus, User, Caravan, Baby, PersonStanding } from 'lucide-react';
import styles from './VehicleSelector.module.css';

export interface VehiculosCounts {
    coches: string;
    motos: string;
    camiones: string;
    autobuses: string;
    remolques: string;
}

export interface PasajerosCounts {
    adultos: string;
    niños: string;
    bebes: string;
}

interface VehicleSelectorProps {
    vehiculos: VehiculosCounts;
    onVehiculosChange: (v: VehiculosCounts) => void;
    pasajeros: PasajerosCounts;
    onPasajerosChange: (p: PasajerosCounts) => void;
    numeroCoche: string;
    onNumeroCocheChange: (text: string) => void;
}

type VehicleKey = keyof VehiculosCounts;
type PassengerKey = keyof PasajerosCounts;

const VEHICLES_TOP: { key: VehicleKey; label: string; icon: typeof Car }[] = [
    { key: 'coches', label: 'Coche', icon: Car },
    { key: 'motos', label: 'Moto', icon: Motorcycle },
    { key: 'camiones', label: 'Camión', icon: Truck },
];

const VEHICLES_AFTER_BABY: { key: VehicleKey; label: string; icon: typeof Car }[] = [
    { key: 'autobuses', label: 'Autobús', icon: Bus },
    { key: 'remolques', label: 'Remolque', icon: Caravan },
];

const PASSENGERS: { key: PassengerKey; label: string; icon: typeof User }[] = [
    { key: 'adultos', label: 'Adulto', icon: User },
    { key: 'niños', label: 'Niño', icon: PersonStanding },
    { key: 'bebes', label: 'Bebé', icon: Baby },
];

export default function VehicleSelector({
    vehiculos,
    onVehiculosChange,
    pasajeros,
    onPasajerosChange,
    numeroCoche,
    onNumeroCocheChange,
}: VehicleSelectorProps) {

    const incVehicle = (key: VehicleKey) => {
        const cur = parseInt(vehiculos[key] || '0', 10);
        if (cur < 99) onVehiculosChange({ ...vehiculos, [key]: String(cur + 1) });
    };

    const decVehicle = (key: VehicleKey) => {
        const cur = parseInt(vehiculos[key] || '0', 10);
        if (cur > 0) onVehiculosChange({ ...vehiculos, [key]: String(cur - 1) });
    };

    const incPassenger = (key: PassengerKey) => {
        const cur = parseInt(pasajeros[key] || '0', 10);
        if (cur < 99) onPasajerosChange({ ...pasajeros, [key]: String(cur + 1) });
    };

    const decPassenger = (key: PassengerKey) => {
        const cur = parseInt(pasajeros[key] || '0', 10);
        if (cur > 0) onPasajerosChange({ ...pasajeros, [key]: String(cur - 1) });
    };

    const vehicleCol = (key: VehicleKey, Icon: typeof Car, label: string) => {
        const count = parseInt(vehiculos[key] || '0', 10);
        return (
            <div className={styles.btnCol}>
                <span className={styles.colCaption} title={label}>{label}</span>
                <button className={styles.iconBtn} onClick={() => incVehicle(key)} type="button">
                    <Icon size={20} color="#002B45" />
                    {count > 0 && <span className={styles.badge}>{count}</span>}
                </button>
                {count > 0 ? (
                    <button className={styles.subtractBtn} onClick={() => decVehicle(key)} type="button">
                        <Minus size={14} color="currentColor" strokeWidth={2.25} />
                    </button>
                ) : (
                    <div className={styles.subtractPlaceholder} />
                )}
            </div>
        );
    };

    const passengerCol = (key: PassengerKey, Icon: typeof User, label: string) => {
        const count = parseInt(pasajeros[key] || '0', 10);
        return (
            <div className={styles.btnCol}>
                <span className={styles.colCaption} title={label}>{label}</span>
                <button className={styles.iconBtn} onClick={() => incPassenger(key)} type="button">
                    <Icon size={20} color="#002B45" />
                    {count > 0 && <span className={styles.badge}>{count}</span>}
                </button>
                {count > 0 ? (
                    <button className={styles.subtractBtn} onClick={() => decPassenger(key)} type="button">
                        <Minus size={14} color="currentColor" strokeWidth={2.25} />
                    </button>
                ) : (
                    <div className={styles.subtractPlaceholder} />
                )}
            </div>
        );
    };

    return (
        <div className={styles.grid}>
            {/* Fila 1: ID = 2 columnas + 3 vehículos */}
            <div className={styles.idCell}>
                <div className={styles.idStack}>
                    <span className={styles.idCaption} title="Número de identificación del vehículo">
                        ID
                    </span>
                    <input
                        className={styles.idInput}
                        placeholder="000"
                        aria-label="Número de identificación del vehículo"
                        value={numeroCoche}
                        onChange={e => {
                            const t = e.target.value;
                            if (t === '' || (/^\d+$/.test(t) && t.length <= 4)) onNumeroCocheChange(t);
                        }}
                        inputMode="numeric"
                        maxLength={4}
                    />
                    <div className={styles.idSubtractSpacer} aria-hidden />
                </div>
            </div>
            {VEHICLES_TOP.map(({ key, icon: Icon, label }) => (
                <div key={key} className={styles.gridCell}>
                    {vehicleCol(key, Icon, label)}
                </div>
            ))}

            {/* Fila 2: 5 columnas alineadas con la fila 1 */}
            {PASSENGERS.map(({ key, icon: Icon, label }) => (
                <div key={key} className={styles.gridCell}>
                    {passengerCol(key, Icon, label)}
                </div>
            ))}
            {VEHICLES_AFTER_BABY.map(({ key, icon: Icon, label }) => (
                <div key={key} className={styles.gridCell}>
                    {vehicleCol(key, Icon, label)}
                </div>
            ))}
        </div>
    );
}
